import { useTranslation } from 'react-i18next';
import { useApi } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { Table, THead, TH, TR, TD } from '@/components/ui/table';
import { formatQuantity } from '../shared';

/**
 * C-11 — `GET /inventory/reports/cogs/:orderItemId`, the cost of one sold line
 * read back from the stock it consumed (§21).
 *
 * `components` is empty whenever the sale wrote no `SALE_CONSUMPTION` movement
 * — which is every line of a catalog item with no recipe attached. The server
 * still answers `cost: "0.00"` and `marginPercent: "100.00"` because that is
 * what the arithmetic says, but zero-cost and not-costed are different claims
 * and only one of them is a measurement. `recipeVersionId` is the pin that
 * makes a costed line reproducible.
 *
 * Lifted out of `Orders.tsx` when the board grew a detail view: the panel is
 * where a line is now inspected, and the board file should not own a component
 * it no longer renders.
 */
export interface CogsBreakdown {
  orderItemId: string;
  catalogItemId: string;
  name: string;
  quantity: string;
  revenue: string;
  cost: string;
  margin: string;
  marginPercent: string | null;
  recipeVersionId: string | null;
  components: {
    inventoryItemId: string;
    name: string;
    /** Consumed amount, positive — the movement itself is negative. */
    quantity: string;
    /** `Decimal(12,4)`: a gram of an ingredient costs fractions of a minor unit. */
    unitCost: string;
    cost: string;
  }[];
}

export function CogsDetail({ orderItemId, branchId }: { orderItemId: string; branchId: string }) {
  const { t, i18n } = useTranslation();
  const { data, loading, error, reload } = useApi<CogsBreakdown>(
    `/inventory/reports/cogs/${orderItemId}`,
  );
  // The order's own branch, not the header switcher — a line is denominated in
  // the currency of the branch that sold it.
  const { format: money, code, info } = useCurrency(branchId || undefined);

  /**
   * A per-unit ingredient cost carries four decimals, more than any currency's
   * minor unit. Rounding it to the currency's scale would print "0.00" for a
   * real cost, so the currency and its minor unit still come from `useCurrency`
   * — only the *maximum* digits are widened to keep the figure it was given.
   */
  const unitCost = (value: string) =>
    new Intl.NumberFormat(i18n.language || 'en', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: info.decimals,
      maximumFractionDigits: Math.max(info.decimals, 4),
    }).format(Number(value));

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  // Nothing was consumed, so there is no cost to report. Showing the server's
  // 0.00 / 100% here would read as "this line cost nothing to make", which is a
  // claim the data does not support.
  if (!data.components.length) {
    return (
      <div className="space-y-1 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3 text-sm text-amber-900 dark:text-amber-200">
        <p className="font-medium">{t('operations.orders.cogs.notCosted')}</p>
        <p>{t('operations.orders.cogs.notCostedHint')}</p>
        <p className="text-amber-800 dark:text-amber-300">
          {t('operations.orders.cogs.revenue')}: {money(data.revenue)}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span>
          <span className="text-ink-muted">{t('operations.orders.cogs.revenue')}: </span>
          <span className="font-medium tabular-nums">{money(data.revenue)}</span>
        </span>
        <span>
          <span className="text-ink-muted">{t('operations.orders.cogs.cost')}: </span>
          <span className="font-medium tabular-nums">{money(data.cost)}</span>
        </span>
        <span>
          <span className="text-ink-muted">{t('operations.orders.cogs.margin')}: </span>
          <span className="font-medium tabular-nums">
            {money(data.margin)}
            {data.marginPercent !== null &&
              ` (${new Intl.NumberFormat(i18n.language || 'en', {
                maximumFractionDigits: 1,
              }).format(Number(data.marginPercent))}%)`}
          </span>
        </span>
      </div>

      <Table>
        <THead>
          <TR>
            <TH>{t('operations.orders.cogs.component')}</TH>
            <TH>{t('common.quantity')}</TH>
            <TH className="text-end">{t('operations.orders.cogs.unitCost')}</TH>
            <TH className="text-end">{t('operations.orders.cogs.cost')}</TH>
          </TR>
        </THead>
        <tbody>
          {data.components.map((component) => (
            <TR key={component.inventoryItemId}>
              <TD>{component.name}</TD>
              <TD>{formatQuantity(component.quantity)}</TD>
              <TD className="text-end tabular-nums">{unitCost(component.unitCost)}</TD>
              <TD className="text-end tabular-nums">{money(component.cost)}</TD>
            </TR>
          ))}
        </tbody>
      </Table>

      {data.recipeVersionId && (
        <p className="text-xs text-ink-muted">
          {t('operations.orders.cogs.recipeVersion', { id: data.recipeVersionId })}
        </p>
      )}
    </div>
  );
}
